import { useQuery } from '@tanstack/react-query'
import { Building2, Check, ChevronDown } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@aeris/ui'

import { fetchBranches, fetchCompanies } from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * Active company and branch.
 *
 * The context lives on the session row, not in the browser: switching is a
 * server call, so it survives a reload, applies to every request the session
 * makes, and leaves an audit trail. Keeping it client-side would mean each tab
 * could be acting in a different company while the server believed otherwise —
 * which in an ERP means a document posted to the wrong place.
 *
 * The lists are fetched inside the tenant scope, so a company from another
 * tenant cannot appear here even if something upstream went wrong.
 */

/**
 * What the topbar shows.
 *
 * The trade name, falling back to the legal name. An operator recognises
 * "Elite Digital"; "Elite Digital Comércio e Serviços LTDA" only fits a topbar
 * by being truncated to the point of being unreadable.
 */
const labelFor = (context: {
  companyName: string | null
  companyTradeName: string | null
  branchName: string | null
}): string => {
  const company = context.companyTradeName ?? context.companyName
  if (company === null) return 'Selecionar empresa'
  return context.branchName === null
    ? company
    : `${company} · ${context.branchName}`
}

export const ContextSwitcher = ({
  onBeforeSwitch,
}: {
  /**
   * Pergunta antes de trocar, e troca só com `true` (F138).
   *
   * A área de trabalho do Modo PC usa para confirmar quando há ficha aberta:
   * trocar de loja remonta as janelas, e o que estava sendo digitado se perde.
   */
  onBeforeSwitch?: (() => Promise<boolean>) | undefined
} = {}): ReactNode => {
  const { session, switchContext: switchNow } = useAuth()
  const [open, setOpen] = useState(false)
  const context = session?.context ?? null

  const switchContext = async (input: {
    tenantId: string
    companyId: string | null
    branchId: string | null
  }): Promise<void> => {
    if (onBeforeSwitch !== undefined && !(await onBeforeSwitch())) return
    await switchNow(input)
  }

  // Fetched only while the menu is open. This sits in the topbar of every
  // screen; loading two lists on every page load to populate a menu nobody
  // opened is exactly the kind of waste Master Spec section 27 rules out.
  const companies = useQuery({
    queryKey: ['companies', context?.tenantId],
    queryFn: ({ signal }) => fetchCompanies(signal),
    enabled: open && context !== null,
    staleTime: 5 * 60_000,
  })

  const branches = useQuery({
    queryKey: ['branches', context?.tenantId, context?.companyId],
    queryFn: ({ signal }) =>
      fetchBranches(context?.companyId ?? undefined, signal),
    enabled: open && context?.companyId != null,
    staleTime: 5 * 60_000,
  })

  /*
   * No context yet: offer the memberships instead of a dead end.
   *
   * The API selects a context automatically when somebody belongs to exactly
   * one tenant, so this branch used to be unreachable in practice and rendered
   * the words "Nenhuma conta ativa" with nothing to click. The moment a second
   * tenant existed — Vibora Labs, alongside Elite Digital — every fresh login
   * by that account landed in an application with an empty sidebar and no way
   * forward. The session that was already carrying a context kept working,
   * which is what hid it.
   *
   * `memberships` has been on the session payload since F006 and no screen had
   * ever read it.
   */
  if (context === null) {
    const memberships = session?.memberships ?? []

    if (memberships.length === 0) {
      return (
        <span className="text-(length:--text-xs) text-(--text-muted)">
          Nenhuma conta ativa
        </span>
      )
    }

    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={
              <Building2 aria-hidden="true" className="h-3.5 w-3.5" />
            }
            className="max-w-72 justify-start"
          >
            <span className="truncate">Escolher conta</span>
            <ChevronDown
              aria-hidden="true"
              className="ml-1 h-3.5 w-3.5 shrink-0"
            />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="start" className="w-72">
          <p className="px-2 py-1.5 text-(length:--text-xs) font-(--weight-medium) text-(--text-muted)">
            Conta
          </p>
          {memberships.map((membership) => (
            <DropdownMenuItem
              key={membership.tenantId}
              onSelect={() => {
                // Company and branch are left for the server to settle: it
                // knows which ones this tenant has and this component does not
                // — its company query is scoped to a tenant that is not chosen
                // yet.
                void switchContext({
                  tenantId: membership.tenantId,
                  companyId: null,
                  branchId: null,
                })
              }}
            >
              {membership.tenantName}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  const choose = (companyId: string, branchId: string | null): void => {
    void switchContext({
      tenantId: context.tenantId,
      companyId,
      // Changing company invalidates the branch: a branch belongs to one
      // company, and carrying the old one over would be an incoherent context.
      branchId,
    })
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Building2 aria-hidden="true" className="h-3.5 w-3.5" />}
          className="max-w-72 justify-start"
        >
          <span className="truncate">
            <span className="sr-only">Contexto ativo: </span>
            {labelFor(context)}
          </span>
          <ChevronDown
            aria-hidden="true"
            className="ml-1 h-3.5 w-3.5 shrink-0"
          />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        <p className="px-2 py-1.5 text-(length:--text-xs) font-(--weight-medium) text-(--text-muted)">
          Empresa
        </p>

        {companies.isPending ? (
          <p className="px-2 py-1.5 text-(length:--text-xs) text-(--text-muted)">
            Carregando…
          </p>
        ) : companies.isError ? (
          <p className="px-2 py-1.5 text-(length:--text-xs) text-(--danger)">
            Não foi possível carregar as empresas.
          </p>
        ) : companies.data.companies.length === 0 ? (
          <p className="px-2 py-1.5 text-(length:--text-xs) text-(--text-muted)">
            Nenhuma empresa cadastrada.
          </p>
        ) : (
          companies.data.companies.map((company) => (
            <DropdownMenuItem
              key={company.id}
              onSelect={() => {
                choose(company.id, null)
              }}
            >
              {company.id === context.companyId ? (
                <Check aria-hidden="true" className="h-4 w-4" />
              ) : (
                <span aria-hidden="true" className="h-4 w-4" />
              )}
              <span className="truncate">
                {company.tradeName ?? company.legalName}
              </span>
            </DropdownMenuItem>
          ))
        )}

        {context.companyId === null ? null : (
          <>
            <DropdownMenuSeparator />
            <p className="px-2 py-1.5 text-(length:--text-xs) font-(--weight-medium) text-(--text-muted)">
              Filial
            </p>

            {branches.isPending ? (
              <p className="px-2 py-1.5 text-(length:--text-xs) text-(--text-muted)">
                Carregando…
              </p>
            ) : branches.isError ? (
              <p className="px-2 py-1.5 text-(length:--text-xs) text-(--danger)">
                Não foi possível carregar as filiais.
              </p>
            ) : (
              branches.data.branches.map((branch) => (
                <DropdownMenuItem
                  key={branch.id}
                  onSelect={() => {
                    choose(context.companyId ?? '', branch.id)
                  }}
                >
                  {branch.id === context.branchId ? (
                    <Check aria-hidden="true" className="h-4 w-4" />
                  ) : (
                    <span aria-hidden="true" className="h-4 w-4" />
                  )}
                  <span className="truncate">
                    {branch.code} — {branch.name}
                  </span>
                </DropdownMenuItem>
              ))
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
